import type { UnverifiedValue } from "../services/busbarSelection";
import type {
  BusbarSelectionStatus,
  CheckKey,
  CheckStatus,
} from "../services/busbarSelectionContract";

// The words of the busbar selection result, typed against the contract enums so a value the
// contract gains cannot pass without a label. Display only: no status is derived, recoloured
// or hidden here; every word is the engine's status said in plain language.

// The same words as the Transformer, DG, UPS, PV, starter, breaker and busbar evidence
// summaries for a warned and a review-required study: missing evidence is a review the
// engineer owes, not a design failure. A study is never VALID (the ratings are declared, A16
// (d)). NO_SOLUTION is this module's own: no arrangement or no catalogue row meets a check.
export const STUDY_STATUS_LABELS: Record<BusbarSelectionStatus, string> = {
  WARNING: "Calculated with warnings",
  REVIEW_REQUIRED: "Engineering review required",
  NO_SOLUTION: "Busbar selection not met",
};

export const STUDY_STATUS_TONE: Record<BusbarSelectionStatus, "pass" | "fail" | "warning"> = {
  WARNING: "warning",
  REVIEW_REQUIRED: "warning",
  NO_SOLUTION: "fail",
};

// The checks of KES-EL-BUS-002, named as the standard names them.
export const CHECK_LABELS: Record<CheckKey, string> = {
  screening: "Preliminary area and arrangement",
  continuous_current: "Continuous current",
  temperature_rise: "Temperature rise",
  short_time_withstand: "Short-time withstand Icw",
  peak_withstand: "Peak withstand Ipk",
  neutral: "Neutral bar",
  protective_earth: "Protective earth bar",
  clearance: "Clearances and creepage",
  voltage_drop: "Voltage drop",
  supports: "Bus supports",
};

// The word of a check (KES-EL-BUS-002 section 17). VALID is the only one said as a pass;
// REVIEW_REQUIRED is a review the engineer owes and is never worded as one.
export const CHECK_STATUS_LABELS: Record<CheckStatus, string> = {
  VALID: "Pass",
  REVIEW_REQUIRED: "Review required",
  NO_SOLUTION: "Failed",
  NOT_APPLICABLE: "Not applicable",
};

// The order the counts and the legend list the statuses in: what needs the engineer first.
export const CHECK_STATUS_ORDER: readonly CheckStatus[] = [
  "NO_SOLUTION",
  "REVIEW_REQUIRED",
  "VALID",
  "NOT_APPLICABLE",
];

// The key of a check's outcome cell (td[data-check-result]) and its tone, shared with the
// other modules' result tables. Only VALID is a pass.
const RESULT_KEYS: Record<CheckStatus, "pass" | "fail" | "review" | "not-evaluated"> = {
  VALID: "pass",
  REVIEW_REQUIRED: "review",
  NO_SOLUTION: "fail",
  NOT_APPLICABLE: "not-evaluated",
};

export function checkResultKey(status: CheckStatus): string {
  return RESULT_KEYS[status];
}

// A passed screening is a preliminary area check, never a final pass (founder observation F1,
// 2026-10-01): the screen says so. The status stays VALID.
export const PRELIMINARY_PASS = "Preliminary pass";

/** The word shown for a check: its status word, or "Preliminary pass" for a passed screening. */
export function checkWord(key: CheckKey, status: CheckStatus): string {
  return key === "screening" && status === "VALID" ? PRELIMINARY_PASS : CHECK_STATUS_LABELS[status];
}

// The words the summary counts under, in CHECK_STATUS_ORDER: a passed screening is counted on
// its own as "Preliminary pass", before the other passes (founder decision 2026-10-01).
const COUNT_ORDER: readonly { key: string; word: string }[] = CHECK_STATUS_ORDER.flatMap(
  (status) =>
    status === "VALID"
      ? [
          { key: "PRELIMINARY_PASS", word: PRELIMINARY_PASS },
          { key: status, word: CHECK_STATUS_LABELS[status] },
        ]
      : [{ key: status, word: CHECK_STATUS_LABELS[status] }],
);

/** How many checks stand under each word, each check counted once under the word its row shows. */
export function checkCounts(
  checks: readonly { key: CheckKey; status: CheckStatus }[],
): { key: string; word: string; count: number }[] {
  return COUNT_ORDER.map(({ key, word }) => ({
    key,
    word,
    count: checks.filter((check) => checkWord(check.key, check.status) === word).length,
  }));
}

// A value the engine marked as not verified: the engineer's evidence carries no complete
// document reference (founder rule 2026-10-01). The words and the parts are the engine's; the
// screen only says where they belong.
export const DATA_NOT_VERIFIED = "Data not verified";

export const REFERENCE_PART_WORDS: Record<string, string> = {
  document: "document",
  revision_or_date: "revision or date",
  page_or_clause: "page or clause",
};

/** "Base ampacity 1600 A — missing: page or clause": the tooltip of one marker. */
export function unverifiedTitle(value: UnverifiedValue): string {
  const quantity = value.unit === null ? value.value : `${value.value} ${value.unit}`;
  const missing = value.missing.map((part) => REFERENCE_PART_WORDS[part] ?? part).join(", ");
  return `${value.label} ${quantity} — missing: ${missing}`;
}

/** "2 × 80 × 10 mm": bars per phase and the size of one bar, as the engine sent them. */
export function arrangementText(arrangement: {
  bars: number;
  width_mm: string;
  thickness_mm: string;
}): string {
  return `${arrangement.bars} × ${arrangement.width_mm} × ${arrangement.thickness_mm} mm`;
}
